import { t } from '@framework/i18n/intl/i18next.ts';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { prepareZXingModule } from '@yudiel/react-qr-scanner';
import { Button, Flex, Input, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { useState } from 'react';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { FrontPageTitleContainer } from '#theme';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import Scanner from './ScannerHandler.tsx';
import { VerifyPassForm } from './VerifyPassForm.tsx';
import wasmFile from './zxing_reader.wasm?url';

const MaxWidthContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  ${down('sm')} {
    padding: 0 12px;
  }
`;

const PAGE_TITLE_ID = 'verify-pass-title-focus-id';

export type IVerifyPassInput =
  | { type: 'qrcode'; code: string }
  | { type: 'manual'; code: string };

prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) => {
      if (path.endsWith('.wasm')) {
        return wasmFile;
      }
      return prefix + path;
    },
  },
});

export function VerifyPassPage() {
  const [showInfo, setShowInfo] = useState(false);
  const [code, setCode] = useState<IVerifyPassInput | undefined>();

  useBreadcrumbItem({
    path: '/verifica-tesserini',
    title: t('verifica-tesserino.breadcrumb', 'Verifica Tesserini'),
  });

  return (
    <PageLayout>
      <MaxWidthContainer>
        <FrontPageTitleContainer>
          <Title level={1} tabIndex={-1} id={PAGE_TITLE_ID}>
            {t('verifica-tesserino.titolo', 'Verifica Tesserini')}
          </Title>
        </FrontPageTitleContainer>

        {showInfo ? (
          <VerifyPassForm
            code={code}
            setShowInfo={setShowInfo}
            setCode={setCode}
          />
        ) : (
          <ResponsiveCard>
            <Scanner
              onScan={result => {
                setCode({ type: 'qrcode', code: result[0].rawValue });
                setShowInfo(true);
              }}
              showInfo={showInfo}
            />
            <VerticalSpace size={6} />
            <Typography.Paragraph
              style={{ textAlign: 'center', marginBottom: 0 }}
            >
              {t(
                'verifica-tesserino.description',
                'Inquadra il QR code del tesserino, oppure inserisci il codice del tesserino nel campo successivo.',
              )}
            </Typography.Paragraph>
            <VerticalSpace size={6} />
            <Flex vertical align="center">
              <Flex vertical gap={12} style={{ maxWidth: 350, width: '100%' }}>
                <Input
                  size="large"
                  placeholder={t(
                    'verifica-tesserino.codice-tesserino-manualmente',
                    'Codice tesserino (inserimento manuale)',
                  )}
                  onChange={e =>
                    setCode({ type: 'manual', code: e.target.value })
                  }
                />
                <Button
                  block
                  size="large"
                  type="primary"
                  disabled={
                    !code || (code.type === 'manual' && code.code.trim() === '')
                  }
                  onClick={() => setShowInfo(true)}
                >
                  {t('verifica-tesserino.cerca', 'Cerca')}
                </Button>
              </Flex>
            </Flex>
          </ResponsiveCard>
        )}
        <VerticalSpace size={48} />
      </MaxWidthContainer>
    </PageLayout>
  );
}
